import {
  QueryObserverResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { getIsStrictlyUSEvent } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BoostedSale,
  CampaignManagerClient,
  Feature,
  Permission,
  SponsoredListingsCampaign,
  Venue,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useClientLogContext } from '../ClientLogContext';
import { useBoostedEventIdsFromCampaigns } from './useBoostedEventIdsFromCampaigns ';

const BASE_QUERY_KEY = 'AdCampaignsDataContext.getCampaignData';

/**
 * Basic sales aggregate data structure.
 */
export type SalesAggregate = {
  saleCount: number;
  totalCost: number;
  totalNetProceeds: number;
  currencyCode: string;
};

/**
 * Aggregated sales data for a campaign, combining all sales from all flights under the campaign.
 * Contains the total cost, total net proceeds, currency code, and per-flight breakdown.
 */
export type CampaignSalesAggregate = SalesAggregate & {
  /**
   * Aggregated sales data broken down by flight ID under this campaign.
   * Each flight's aggregate includes total cost, total net proceeds, and currency code from all sales in that flight.
   */
  flightsAggregate: Record<number, SalesAggregate>;
};

export type IAdCampaignsDataContext = {
  adCampaignsDataQuery: UseQueryResult<Record<
    number,
    SponsoredListingsCampaign[]
  > | null>;
  hasCampaignsQuery: UseQueryResult<boolean | null>;
  isEventsEnabledForSponsoredListingsLoading: (
    eventId: number | null
  ) => boolean;
  allCampaignData:
    | Record<number, SponsoredListingsCampaign[]>
    | null
    | undefined;
  /**
   * Aggregated sales data by campaign ID. Each campaign's aggregate includes
   * total cost, total net proceeds, and currency code from all sales across all flights.
   * Usage: campaignSalesAggregates[campaignId] to get aggregate for a specific campaign.
   */
  campaignSalesAggregates?: Record<number, CampaignSalesAggregate> | null;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  refreshData: () => Promise<QueryObserverResult>;
};

export const AdCampaignsDataContext = createContext<IAdCampaignsDataContext>({
  hasCampaignsQuery: {} as UseQueryResult<boolean | null>,
  adCampaignsDataQuery: {} as UseQueryResult<Record<
    number,
    SponsoredListingsCampaign[]
  > | null>,
  isEventsEnabledForSponsoredListingsLoading: () => false,
  allCampaignData: [],
  campaignSalesAggregates: {},
  refreshData: () => Promise.resolve({} as QueryObserverResult),
});

export const AdCampaignsDataContextProvider = ({
  children,
  loadSalesDetails,
}: {
  children: React.ReactNode;
  loadSalesDetails?: boolean;
}) => {
  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  const hasSponsoredListingsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2
  );

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const { data } = useCatalogDataContext();
  const eventIds = useMemo(() => {
    return Object.values(data?.events || {})
      .filter((ev) => {
        const venue = data?.venues?.[ev.event?.venId ?? ''] || ({} as Venue);
        return (
          ev?.event?.viagId != null && getIsStrictlyUSEvent(venue.ctryCode)
        );
      })
      .map((ev) => Number(ev.event.viagId))
      .filter((id): id is number => id != null)
      .toSorted();
  }, [data?.events, data?.venues]);

  const {
    hasCampaignsQuery,
    campaignDataQuery,
    isEventsEnabledForSponsoredListingsLoading,
    allCampaignData,
    campaignSalesAggregates,
    refreshData,
  } = useGetCampaignDataQuery(eventIds, setErrorInfo, loadSalesDetails);

  useBoostedEventIdsFromCampaigns(allCampaignData);

  if (!hasSponsoredListingsFeature || !hasAdPlatformViewPermission) {
    return <>{children}</>;
  }

  return (
    <AdCampaignsDataContext.Provider
      value={{
        hasCampaignsQuery,
        isEventsEnabledForSponsoredListingsLoading,
        adCampaignsDataQuery: campaignDataQuery,
        allCampaignData,
        campaignSalesAggregates,
        errorInfo,
        refreshData,
      }}
    >
      {children}
    </AdCampaignsDataContext.Provider>
  );
};

export const useAdCampaignsDataContext = () =>
  useContext(AdCampaignsDataContext);

export const useGetCampaignDataQuery = (
  eventIds: number[],
  setErrorInfo: (
    value: React.SetStateAction<
      | {
          errorHeader: React.ReactNode;
          errorMessage: React.ReactNode;
        }
      | undefined
    >
  ) => void,
  loadSalesDetails?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const hasSponsoredListingsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2
  );
  const hasBoostTabFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_EventDetailBoostTab
  );

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const campaignDataQueryKey = useMemo(
    () => [
      BASE_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
      eventIds.toSorted(),
      loadSalesDetails,
    ],
    [activeAccountWebClientConfig.activeAccountId, eventIds, loadSalesDetails]
  );

  const shouldQueryCampaignData = useMemo(
    () =>
      hasSponsoredListingsFeature &&
      hasAdPlatformViewPermission &&
      activeAccountWebClientConfig.activeAccountId != null &&
      eventIds.length > 0,
    [
      activeAccountWebClientConfig.activeAccountId,
      eventIds.length,
      hasSponsoredListingsFeature,
      hasAdPlatformViewPermission,
    ]
  );

  const campaignDataQuery = useQuery({
    queryKey: campaignDataQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);

      if (!shouldQueryCampaignData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const data = await client.getCampaigns(eventIds, loadSalesDetails);
          return data.reduce(
            (acc, campaign) => {
              if (acc[campaign.eventId] != null) {
                acc[campaign.eventId].push(campaign);
              } else {
                acc[campaign.eventId] = [campaign];
              }
              return acc;
            },
            {} as Record<number, SponsoredListingsCampaign[]>
          );
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.getCampaignData', error, {
            eventIds,
          });
        }
      );
    },
    enabled: shouldQueryCampaignData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const loadSalesDetailsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.getBoostedSalesByEventIds',
      activeAccountWebClientConfig.activeAccountId,
      eventIds,
      loadSalesDetails,
    ],
    [activeAccountWebClientConfig.activeAccountId, eventIds, loadSalesDetails]
  );

  const shouldQueryLoadSalesDetails = useMemo(
    () =>
      hasBoostTabFeature &&
      hasAdPlatformViewPermission &&
      loadSalesDetails &&
      Boolean(eventIds?.length) &&
      activeAccountWebClientConfig.activeAccountId != null,
    [
      hasBoostTabFeature,
      hasAdPlatformViewPermission,
      loadSalesDetails,
      eventIds?.length,
      activeAccountWebClientConfig.activeAccountId,
    ]
  );

  const loadSalesDetailsQuery = useQuery({
    queryKey: loadSalesDetailsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);

      if (!shouldQueryLoadSalesDetails) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const data = await client.getBoostedSalesByEventIds(eventIds!);
          const boostedSaleByFlights = data.reduce(
            (acc, sale) => {
              if (acc[sale.adCampaignFlightId] != null) {
                acc[sale.adCampaignFlightId].push(sale);
              } else {
                acc[sale.adCampaignFlightId] = [sale];
              }
              return acc;
            },
            {} as Record<number, BoostedSale[]>
          );

          return boostedSaleByFlights;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.getBoostedSalesByEventIds', error, {
            eventIds,
          });
        }
      );
    },
    enabled: shouldQueryLoadSalesDetails,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const hasCampaignsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.hasAnyCampaign',
      activeAccountWebClientConfig.activeAccountId,
    ],
    [activeAccountWebClientConfig.activeAccountId]
  );

  const hasCampaignsQuery = useQuery({
    queryKey: hasCampaignsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);

      if (!shouldQueryCampaignData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const data = await client.hasAnyCampaign();
          return data;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.hasAnyCampaign', error);
        }
      );
    },
    enabled: shouldQueryCampaignData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const eventsEnabledForSponsoredListingsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.getEventsEnabledForSponsoredListings',
      activeAccountWebClientConfig.activeAccountId,
    ],
    [activeAccountWebClientConfig.activeAccountId]
  );

  const shouldQueryHasEventsEnabledForSponsoredListings = useMemo(
    () => activeAccountWebClientConfig.activeAccountId != null,
    [activeAccountWebClientConfig.activeAccountId]
  );

  const eventsEnabledForSponsoredListingsQuery = useQuery({
    queryKey: eventsEnabledForSponsoredListingsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);
      if (!shouldQueryHasEventsEnabledForSponsoredListings) {
        return null;
      }
      return tryInvokeApi(async () => {
        const client = new CampaignManagerClient(activeAccountWebClientConfig);
        const data = await client.getEventsEnabledForSponsoredListings();
        return {
          blackList: data.item1,
          whiteList: data.item2,
        };
      });
    },
    enabled: shouldQueryHasEventsEnabledForSponsoredListings,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const isEventsEnabledForSponsoredListingsLoading = (
    eventId: number | null
  ) => {
    if (eventId == null) {
      return false;
    }

    if (eventsEnabledForSponsoredListingsQuery.isLoading) {
      return false;
    }

    const blackList =
      eventsEnabledForSponsoredListingsQuery.data?.blackList || [];
    const whiteList =
      eventsEnabledForSponsoredListingsQuery.data?.whiteList || [];

    // If in black-list, then disabled
    if (blackList.includes(eventId)) {
      return false;
    }

    if (whiteList.length > 0) {
      // If we have any in white-list, only those are enabled
      // the rest are disabled
      if (whiteList.includes(eventId)) {
        return true;
      }

      return false;
    }

    // If we have no white-list, and not in black-list, then enabled
    return true;
  };

  const queryClient = useQueryClient();

  const refreshData = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [BASE_QUERY_KEY, activeAccountWebClientConfig.activeAccountId],
    });
    queryClient.invalidateQueries({
      queryKey: hasCampaignsQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: loadSalesDetailsQueryKey,
    });
    await hasCampaignsQuery.refetch();
    await loadSalesDetailsQuery.refetch();
    return await campaignDataQuery.refetch();
  }, [
    activeAccountWebClientConfig.activeAccountId,
    campaignDataQuery,
    hasCampaignsQuery,
    hasCampaignsQueryKey,
    loadSalesDetailsQuery,
    loadSalesDetailsQueryKey,
    queryClient,
  ]);

  const campaignSalesAggregates = useMemo(() => {
    if (!campaignDataQuery.data || !loadSalesDetailsQuery.data) {
      return null;
    }

    const aggregates: Record<number, CampaignSalesAggregate> = {};

    // Iterate through all campaigns to aggregate sales data
    Object.values(campaignDataQuery.data)
      .flat()
      .forEach((campaign) => {
        if (!campaign.campaignFlightIds) {
          return;
        }

        let campaignSaleCount = 0;
        let campaignTotalCost = 0;
        let campaignTotalNetProceeds = 0;
        const currencyCode = campaign.currencyCode;
        const flightsAggregate: Record<number, SalesAggregate> = {};

        // Aggregate sales from all flights under this campaign
        campaign.campaignFlightIds.forEach((flightId) => {
          const salesForFlight = loadSalesDetailsQuery.data![flightId];

          let flightSaleCount = 0;
          let flightTotalCost = 0;
          let flightTotalNetProceeds = 0;

          if (salesForFlight) {
            salesForFlight.forEach((sale) => {
              flightSaleCount += 1;
              flightTotalCost += sale.totalCost ?? 0;
              flightTotalNetProceeds += sale.totalNetProceeds ?? 0;

              // Also add to campaign totals
              campaignSaleCount += 1;
              campaignTotalCost += sale.totalCost ?? 0;
              campaignTotalNetProceeds += sale.totalNetProceeds ?? 0;
            });
          }

          // Store flight-level aggregate
          flightsAggregate[flightId] = {
            saleCount: flightSaleCount,
            totalCost: flightTotalCost,
            totalNetProceeds: flightTotalNetProceeds,
            currencyCode,
          };
        });

        aggregates[campaign.campaignId] = {
          saleCount: campaignSaleCount,
          totalCost: campaignTotalCost,
          totalNetProceeds: campaignTotalNetProceeds,
          currencyCode,
          flightsAggregate,
        };
      });

    return aggregates;
  }, [campaignDataQuery.data, loadSalesDetailsQuery.data]);

  return {
    campaignDataQuery,
    hasCampaignsQuery,
    loadSalesDetailsQuery,
    isEventsEnabledForSponsoredListingsLoading,
    allCampaignData: campaignDataQuery.data,
    campaignSalesAggregates,
    refreshData: refreshData,
  };
};
